import { z } from "zod";
import { moneySchema } from "./common.js";

/**
 * A fully qualified domain name in lowercase ASCII (punycode for IDNs).
 * Validation is intentionally permissive; the registrar is the authority on registrability.
 */
export const domainNameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(253)
  .regex(/^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9-]{2,63}$/, {
    message: "Must be a valid domain name such as example.com",
  });
export type DomainName = z.infer<typeof domainNameSchema>;

export const tldSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9-]{2,63}(\.[a-z0-9-]{2,63})?$/, {
    message: "Must be a TLD such as com or com.tr",
  });
export type Tld = z.infer<typeof tldSchema>;

export const domainAvailabilitySchema = z.enum([
  "available",
  "unavailable",
  "premium",
  "reserved",
  "unsupported",
  "unknown",
]);
export type DomainAvailability = z.infer<typeof domainAvailabilitySchema>;

/**
 * Price breakdown shown to the customer. Domains are sold at cost:
 * registry wholesale + ICANN fee + payment processing, each visible on the receipt.
 */
export const domainPriceBreakdownSchema = z.object({
  wholesale: moneySchema,
  icannFee: moneySchema,
  processingFee: moneySchema,
  total: moneySchema,
});
export type DomainPriceBreakdown = z.infer<typeof domainPriceBreakdownSchema>;

export const domainCheckRequestSchema = z.object({
  domains: z.array(domainNameSchema).min(1).max(50),
});
export type DomainCheckRequest = z.infer<typeof domainCheckRequestSchema>;

export const domainCheckResultSchema = z.object({
  domain: domainNameSchema,
  availability: domainAvailabilitySchema,
  /** Present when the domain can be registered through us. */
  registration: domainPriceBreakdownSchema.optional(),
  renewal: domainPriceBreakdownSchema.optional(),
  provider: z.string().optional(),
});
export type DomainCheckResult = z.infer<typeof domainCheckResultSchema>;

export const domainCheckResponseSchema = z.object({
  results: z.array(domainCheckResultSchema),
});
export type DomainCheckResponse = z.infer<typeof domainCheckResponseSchema>;

/** Second-level public suffixes we sell under; a full Public Suffix List lookup replaces this later. */
const TWO_LABEL_SUFFIXES = new Set([
  "com.tr",
  "net.tr",
  "org.tr",
  "gen.tr",
  "biz.tr",
  "info.tr",
  "web.tr",
  "co.uk",
  "org.uk",
  "me.uk",
  "com.au",
  "net.au",
  "org.au",
  "co.nz",
  "co.za",
  "com.br",
  "com.mx",
  "co.jp",
  "co.in",
  "com.de",
]);

/** Extracts the TLD (public suffix) portion of a domain name: "example.com.tr" -> "com.tr". */
export function tldOf(domain: string): string {
  const labels = domain.toLowerCase().split(".");
  const lastTwo = labels.slice(-2).join(".");
  if (labels.length >= 3 && TWO_LABEL_SUFFIXES.has(lastTwo)) {
    return lastTwo;
  }
  return labels.at(-1) ?? "";
}

/** The registrable label without its TLD: "shop.example.com.tr" -> "shop.example". */
export function labelOf(domain: string): string {
  const tld = tldOf(domain);
  return domain.toLowerCase().slice(0, -(tld.length + 1));
}

export const tldOfferSchema = z.object({
  tld: tldSchema,
  provider: z.string(),
  registration: domainPriceBreakdownSchema,
  renewal: domainPriceBreakdownSchema,
  transfer: domainPriceBreakdownSchema,
  minYears: z.number().int().min(1),
  maxYears: z.number().int().min(1),
});
export type TldOffer = z.infer<typeof tldOfferSchema>;

export const tldCatalogResponseSchema = z.object({
  tlds: z.array(tldOfferSchema),
  updatedAt: z.iso.datetime(),
});
export type TldCatalogResponse = z.infer<typeof tldCatalogResponseSchema>;
